import React, { useState } from "react";
import {
    Modal,
    Form,
    Input,
    Row,
    Col,
    Button,
    Typography,
    message,
    notification,
    Alert
} from "antd";
import { EyeTwoTone, EyeInvisibleOutlined } from "@ant-design/icons";
import UserApi from "../../common/Apis/UserApi";
import { createDefaultTenantAdminUser } from "../../../models/Tenant/TenantAdminDTO";
import { createDefaultSubTenantAdminUser } from "../../../models/SubTenant/SubTenantAdminUserDTO";
const { Title } = Typography;

const AddAdminUserSubTenant = ({ open, onClose, tenantId }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(null);

    const handleFinish = async (values) => {
        if (!tenantId) {
            return message.error("Tenant ID not found. Please restart the process.");
        }

        const payload = createDefaultSubTenantAdminUser({
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            password: values.password,
            matchingPassword: values.confirmPassword,
            tenantDTO: { id: tenantId },
        });

        try {
            setLoading(true);
            await UserApi.CreateTenantAdmin(payload);

            notification.open({
                message: (
                    <span style={{ fontWeight: "bold" }}>
                        New tenant admin user added successfully!
                    </span>
                ),
                closeIcon: false,
                duration: 3,
                style: {
                    backgroundColor: "#b3ffb3",
                    color: "green",
                },
            });

            form.resetFields();
            onClose();
        } catch (err) {
            setApiError(
                err?.apiError?.message ||
                err?.message ||
                "addind tenant admin error. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onCancel={onClose} footer={null} destroyOnClose>
            <Title level={4} className="text-center mb-3">
                Add New Admin User
            </Title>
            {apiError && (
                <Alert message={apiError} type="error" showIcon className="mb-3" />
            )}
            <Form
                form={form}
                name="admin-form"
                layout="vertical"
                onFinish={handleFinish}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="firstName"
                            label={
                                <span>
                                    First Name <span style={{ color: "red" }}>*</span>
                                </span>
                            }
                            rules={[{ required: true, message: "Please enter first name" }]}
                        >
                            <Input placeholder="First Name" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="lastName"
                            label={
                                <span>
                                    Last Name <span style={{ color: "red" }}>*</span>
                                </span>
                            }
                            rules={[{ required: true, message: "Please enter last name" }]}
                        >
                            <Input placeholder="Last Name" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row>
                    <Col span={24}>
                        <Form.Item
                            name="email"
                            label={
                                <span>
                                    Email Address <span style={{ color: "red" }}>*</span>
                                </span>
                            }
                            rules={[
                                { required: true, message: "Please enter email address" },
                                { type: "email", message: "Please enter a valid email address" },
                            ]}
                        >
                            <Input placeholder="Email address" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="password"
                            label={
                                <span>
                                    Password <span style={{ color: "red" }}>*</span>
                                </span>
                            }
                            rules={[{ required: true, message: "Please enter password" }]}
                        >
                            <Input.Password
                                placeholder="Password"
                                iconRender={(visible) =>
                                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                                }
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="confirmPassword"
                            label={
                                <span>
                                    Re-enter Password <span style={{ color: "red" }}>*</span>
                                </span>
                            }
                            dependencies={["password"]}
                            rules={[
                                { required: true, message: "Please re-enter password" },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue("password") === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error("Passwords do not match"));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password
                                placeholder="Re-enter Password"
                                iconRender={(visible) =>
                                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                                }
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <div className="d-flex justify-content-end pt-3">
                    <Button onClick={onClose} style={{ marginRight: "10px" }}>
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        Save
                    </Button>
                </div>
            </Form>
        </Modal>
    );
};

export default AddAdminUserSubTenant;
